<script setup>
import { ref, reactive, onMounted, defineProps, defineEmits, watch } from "vue";
import { apiQuery } from "@/api/j24api";
import VSelectSearch from "@/components/VSelectSearch.vue";
import FileStructure from "./FileStructure.vue";
import ElevateESFShellCommands from "./ElevateESFShellCommands.vue";
import ElevateESFEventDetail from "./ElevateESFEventDetail.vue";
import ElevateESFProfiles from "./ElevateESFProfiles.vue";

const props = defineProps({
  licenceName: String,
  eventReference: String,
  username: String,
  serialNumber: String,
});
const emit = defineEmits(["back"]);

const loading = ref(false);
const selectedTab = ref("overview");
const logEvents = [];
const signingIdMenu = ref(false);
const eventTypesMenu = ref(false);
const appChangesPresent = ref(false);

onMounted(() => {
  getEvent();
});

watch(
  () => props.eventReference,
  () => {
    getEvent();
  }
);

async function getEvent() {
  loading.value = true;
  var gotAllEvents = false;
  var events = [];
  var counter = 0;
  var page = 0;

  let result = await apiQuery("ELA", "elevatereports", {
    apiAction: "ESFReport",
    esfReportAction: "GetEvent",
    elevateLicence: props.licenceName,
    customReference: props.eventReference,
    page: page,
  });
  if (result.events.length > 0) {
    events.push(...result.events);
    console.log(result.pagination);
  }
  if (result.pagination.total <= result.pagination.pageSize) {
    gotAllEvents = true;
  } else {
    // Calculate the number of pages to get all the events
    const pages = Math.ceil(
      result.pagination.total / result.pagination.pageSize
    );

    // Get Each Page
    for (let i = 1; i < pages; i++) {
      let result = await apiQuery("ELA", "elevatereports", {
        apiAction: "ESFReport",
        esfReportAction: "GetEvent",
        elevateLicence: props.licenceName,
        customReference: props.eventReference,
        page: i,
      });
      if (result.events.length > 0) {
        console.log(result.pagination);
        events.push(...result.events);
      }
    }
  }

  console.log("got to here");

  if (events.length > 0) {
    logEvents.length = 0;
    logEvents.push(...events);
  }
  processEvents(events);
  loading.value = false;
}

const eventTypes = ref({});
const filteredEvents = reactive([]);
const signingIds = ref({});
const eventHeaders = [
  { text: "Event Type", value: "eventtype", sortable: true },
  { text: "Apple Process", value: "isplatform", sortable: true },
  { text: "Signing ID", value: "signingid", sortable: true },
  //   { text: "Process", value: "processpath", sortable: true },
  { text: "Username", value: "username", sortable: true },
  { text: "Target", value: "target", sortable: true },
];
const applicationHeaders = [
  { text: "Event Type", value: "eventtype", sortable: true },
  { text: "Application Path", value: "path", sortable: true },
  { text: "Version", value: "CFBundleVersion", sortable: true },
];

function processEvents(events) {
  eventTypes.value = {};
  signingIds.value = {};
  events.forEach((event) => {
    if (!event.signingid) {
      event.signingid = "None";
    }
  });

  // Sort the events into types, add a count of events for each type. Include the events in the type
  events.forEach((event) => {
    if (eventTypes.value[event.eventtype]) {
      eventTypes.value[event.eventtype].count++;
      eventTypes.value[event.eventtype].events.push(event);
    } else {
      eventTypes.value[event.eventtype] = {
        count: 1,
        events: [event],
        show: true,
      };
    }
    if (signingIds.value[event.signingid]) {
      signingIds.value[event.signingid].count++;
    } else {
      signingIds.value[event.signingid] = {
        count: 1,
        show: true,
      };
    }
  });
  let sortedSigningIds = Object.entries(signingIds.value).sort(
    (a, b) => b[1].count - a[1].count
  );

  signingIds.value = Object.fromEntries(sortedSigningIds);
  updateFilteredEvents();

  // Check for app changes
  if (
    eventTypes.value["Application:Add"] ||
    eventTypes.value["Application:Changed"] ||
    eventTypes.value["Application:Removed"]
  ) {
    appChangesPresent.value = true;
  }
}

function applicationEvents() {
  var appEvents = [];
  if (eventTypes.value["Application:Add"]) {
    appEvents.push(...eventTypes.value["Application:Add"].events);
  }
  if (eventTypes.value["Application:Changed"]) {
    appEvents.push(...eventTypes.value["Application:Changed"].events);
  }
  if (eventTypes.value["Application:Removed"]) {
    appEvents.push(...eventTypes.value["Application:Removed"].events);
  }
  return appEvents;
}

const searchValue = ref("");

function updateFilteredEvents() {
  filteredEvents.length = 0;
  var tmpArray = [];
  Object.keys(eventTypes.value).forEach((key) => {
    if (eventTypes.value[key].show) {
      tmpArray.push(...eventTypes.value[key].events);
    }
  });

  Object.keys(signingIds.value).forEach((key) => {
    if (signingIds.value[key].show == true) {
      filteredEvents.push(
        ...tmpArray.filter((event) => event.signingid == key)
      );
    }
  });
}

function searchFields() {
  return eventHeaders.map((header) => header.value);
}

function clearSelectedEventTypes() {
  Object.keys(eventTypes.value).forEach((key) => {
    eventTypes.value[key].show = false;
  });
  updateFilteredEvents();
}

function selectAllEventTypes() {
  Object.keys(eventTypes.value).forEach((key) => {
    eventTypes.value[key].show = true;
  });
  updateFilteredEvents();
}

function clearSelectedSigningIds() {
  Object.keys(signingIds.value).forEach((key) => {
    signingIds.value[key].show = false;
  });
  updateFilteredEvents();
}

function selectAllSigningIds() {
  Object.keys(signingIds.value).forEach((key) => {
    signingIds.value[key].show = true;
  });
  updateFilteredEvents();
}

function shouldShowField(field, value) {
  if (!value) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  let blockKeys = ["_id", "serialNumber", "licenseName", "__v"];
  if (blockKeys.includes(field)) {
    return false;
  }
  return true;
}

function sortObject(object) {
  let sortedObject = {};
  Object.keys(object)
    .sort()
    .forEach((key) => {
      sortedObject[key] = object[key];
    });
  return sortedObject;
}

function filterFileEvents(events) {
  return events.filter((event) => {
    return (
      event.eventtype == "File:Write" ||
      event.eventtype == "File:Unlink" ||
      event.eventtype == "File:Rename" ||
      event.eventtype == "File:Create" ||
      event.eventtype == "File:Rename:Auth"
    );
  });
}

function getTotal(type) {
  var total = 0;
  try {
    switch (type) {
      case "FilesWritten":
        total = eventTypes.value["File:Write"].count;
        break;
      case "FilesDeleted":
        total = eventTypes.value["File:Unlink"].count;
        break;
      case "Processes":
        total = eventTypes.value["Process:Exec"].count;
        break;
      default:
        break;
    }
  } catch (error) {
    total = 0;
    console.log(error);
  }
  return total;
}
</script>

<template>
  <template v-if="loading">
    <v-row>
      <v-spacer></v-spacer>
      <v-col class="text-center pt-11">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-col>
      <v-spacer></v-spacer>
    </v-row>
  </template>
  <template v-else>
    <v-row>
      <v-col cols="auto" class="">
        <v-btn prepend-icon="mdi-arrow-left" class="" @click="$emit('back')"
          >Back</v-btn
        >
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="auto">
        <h2>{{ props.username }} - {{ props.serialNumber }}</h2>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="mb-2">
        <v-tabs v-model="selectedTab" color="#e7541e">
          <v-tab value="overview">Overview</v-tab>
          <v-tab value="fileChanges">File Changes</v-tab>
          <v-tab value="rawEvents">All Events</v-tab>
        </v-tabs>
      </v-col>
    </v-row>

    <v-window v-model="selectedTab">
      <v-window-item value="overview" class="pa-1">
        <!-- <v-row>
          <v-col cols="3">
            <v-card>
              <v-card-title>Events Recorded</v-card-title>
              <v-card-text class="text-center">
                <h3>{{ logEvents.length }}</h3>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="3">
            <v-card>
              <v-card-title>Files Written</v-card-title>
              <v-card-text class="text-center">
                <h3>
                  {{ getTotal("FilesWritten") }}
                </h3>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="3">
            <v-card>
              <v-card-title>Files Deleted</v-card-title>
              <v-card-text class="text-center">
                <h3>{{ getTotal("FilesDeleted") }}</h3>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="3">
            <v-card>
              <v-card-title>Processes Launched</v-card-title>
              <v-card-text class="text-center">
                <h3>{{ getTotal("Processes") }}</h3>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row> -->
        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title><h3>Applications Modified</h3></v-card-title>
              <v-card-text>
                <EasyDataTable
                  :items="applicationEvents()"
                  :headers="applicationHeaders"
                  :loading="loading"
                  :search-field="searchFields()"
                  :search-value="searchValue"
                  empty-message="No Modifications Changes Recorded"
                  theme-color="#e7541e"
                >
                  <template #expand="item">
                    <ElevateESFEventDetail :event="sortObject(item)" />
                  </template>
                </EasyDataTable>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title><h3>Blocked Actions</h3></v-card-title>
              <v-card-text>
                <EasyDataTable
                  :items="logEvents.filter((event) => event.blocked)"
                  :headers="eventHeaders"
                  :loading="loading"
                  :search-field="searchFields()"
                  :search-value="searchValue"
                  empty-message="No Blocked Actions Recorded"
                  theme-color="#e7541e"
                >
                  <template #expand="item">
                    <ElevateESFEventDetail :event="sortObject(item)" />
                  </template>
                </EasyDataTable>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <ElevateESFShellCommands v-if="logEvents.length" :events="logEvents" />

        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title
                ><h3>Configuration Profile Changes</h3></v-card-title
              >
              <v-card-text>
                <ElevateESFProfiles :events="logEvents" />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>

      <v-window-item value="fileChanges">
        <v-card>
          <v-card-text>
            <FileStructure
              v-if="logEvents.length"
              :events="filterFileEvents(logEvents)"
            />
          </v-card-text>
        </v-card>
      </v-window-item>

      <v-window-item value="rawEvents">
        <v-card>
          <v-card-title></v-card-title>
          <v-card-text>
            <v-row>
              <v-col>
                <v-text-field
                  label="Search"
                  variant="underlined"
                  hide-details
                  v-model="searchValue"
                ></v-text-field>
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="auto">
                <v-btn prepend-icon="mdi-filter"
                  >Event Types
                  <v-menu
                    activator="parent"
                    v-model="eventTypesMenu"
                    :close-on-content-click="false"
                  >
                    <v-list>
                      <v-list-item class="pl-2 pa-0">
                        <v-row>
                          <v-col cols="auto">
                            <v-btn
                              prepend-icon="mdi-checkbox-multiple-blank-outline"
                              @click="clearSelectedEventTypes()"
                              variant="text"
                              >Clear All</v-btn
                            >
                          </v-col>
                          <v-col>
                            <v-btn
                              prepend-icon="mdi-checkbox-multiple-outline"
                              variant="text"
                              @click="selectAllEventTypes()"
                              >Select All
                            </v-btn>
                          </v-col>
                          <v-col cols="auto">
                            <v-btn
                              prepend-icon="mdi-close"
                              variant="text"
                              @click="eventTypesMenu = false"
                            >
                              Close</v-btn
                            >
                          </v-col>
                        </v-row>
                      </v-list-item>
                      <v-list-item
                        v-for="key in Object.keys(eventTypes)"
                        :key="key"
                      >
                        <v-checkbox
                          :label="key + ' (' + eventTypes[key].count + ')'"
                          density="compact"
                          hide-details="true"
                          v-model="eventTypes[key].show"
                          @update:modelValue="updateFilteredEvents()"
                        >
                        </v-checkbox>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-btn>
              </v-col>
              <v-col cols="auto">
                <v-btn prepend-icon="mdi-filter" @click="signingIdMenu = !true"
                  >Signing IDs
                  <v-menu
                    activator="parent"
                    v-model="signingIdMenu"
                    :close-on-content-click="false"
                  >
                    <v-list>
                      <v-list-item class="pl-2 pa-0">
                        <v-row>
                          <v-col cols="auto">
                            <v-btn
                              prepend-icon="mdi-checkbox-multiple-blank-outline"
                              @click="clearSelectedSigningIds()"
                              variant="text"
                              >Clear All</v-btn
                            >
                          </v-col>
                          <v-col>
                            <v-btn
                              prepend-icon="mdi-checkbox-multiple-outline"
                              variant="text"
                              @click="selectAllSigningIds()"
                              >Select All
                            </v-btn>
                          </v-col>
                          <v-col cols="auto">
                            <v-btn
                              prepend-icon="mdi-close"
                              variant="text"
                              @click="signingIdMenu = false"
                            >
                              Close</v-btn
                            >
                          </v-col>
                        </v-row>
                      </v-list-item>
                      <v-list-item
                        class="pl-2 pa-0"
                        v-for="key in Object.keys(signingIds).slice(0, 25)"
                        :key="key"
                      >
                        <v-checkbox
                          class="pa-0"
                          :label="key + ' (' + signingIds[key].count + ')'"
                          density="compact"
                          hide-details="true"
                          v-model="signingIds[key].show"
                          @update:modelValue="updateFilteredEvents()"
                        >
                        </v-checkbox>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <EasyDataTable
                  :items="filteredEvents"
                  :headers="eventHeaders"
                  :loading="loading"
                  :search-field="searchFields()"
                  :search-value="searchValue"
                  theme-color="#e7541e"
                >
                  <template #expand="item">
                    <ElevateESFEventDetail :event="sortObject(item)" />
                  </template>
                </EasyDataTable>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-window-item>
    </v-window>
  </template>
</template>
